import { isDeletedRecord } from "./noteDelete"

/**
 * Resolves case id from common API / Redux case payload shapes (full case, work-queue drawer slice, etc.).
 * @param {object|null|undefined} payload
 * @returns {number|null}
 */
export function getCaseIdFromCasePayload(payload) {
    if (!payload || typeof payload !== 'object') {
        return null
    }

    const raw =
        payload.case?.id ??
        payload.case?.case_id ??
        payload.case?.caseId ??
        payload.case_id ??
        payload.caseId ??
        payload.id
    if (raw == null || raw === '') {
        return null
    }
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
        return raw
    }
    const n = Number(raw)
    if (!Number.isNaN(n)) {
        return n
    }
    const s = String(raw).trim()
    return s.length > 0 ? s : null
}

/**
 * Ensures `activeCase.case.id` exists like full getCaseDetails payloads, so shared patient UI works
 * when the work-queue drawer API returns a flattened slice.
 */
export function normalizeActiveCaseShape(caseData) {
    if (!caseData || typeof caseData !== 'object') {
        return caseData
    }
    const id = getCaseIdFromCasePayload(caseData)
    if (id == null) {
        return caseData
    }
    const existingCase = caseData.case && typeof caseData.case === 'object' ? caseData.case : null
    const nestedId = existingCase?.id ?? existingCase?.case_id ?? existingCase?.caseId
    if (nestedId != null && Number(nestedId) === Number(id)) {
        return caseData
    }
    const normalized = {
        ...caseData,
        case: {
            ...(existingCase || {}),
            id: nestedId != null ? nestedId : id,
        },
    }
    if (!Array.isArray(normalized.documents)) {
        normalized.documents = []
    }
    if (!Array.isArray(normalized.procodocs_documents)) {
        normalized.procodocs_documents = []
    }
    return normalized
}

/**
 * Overlay work-queue drawer payload on full case details. The drawer endpoint often omits collections
 * that patient tabs read from Redux (`notes`, `case_sos`) or sends partial `additional`; full case
 * fills those gaps without discarding drawer-specific fields.
 */
export function mergeDrawerCaseWithFullCase(fullCase, drawerSlice) {
    if (!drawerSlice || typeof drawerSlice !== 'object') {
        return fullCase
    }
    if (!fullCase || typeof fullCase !== 'object' || fullCase.errorMessage) {
        return drawerSlice
    }

    const f = fullCase
    const d = drawerSlice
    const merged = { ...f, ...d }

    if (typeof f.additional === 'object' && typeof d.additional === 'object') {
        merged.additional = { ...f.additional, ...d.additional }
    } else if (f.additional && d.additional === undefined) {
        merged.additional = f.additional
    }

    const preserveNotesIfDrawerEmpty = () => {
        const arrD = d.notes
        const arrF = f.notes
        if (!Array.isArray(arrF)) {
            return arrD !== undefined ? arrD : arrF
        }
        if (!Array.isArray(arrD) || arrD.length === 0) {
            return arrF
        }
        return arrD
    }

    merged.notes = preserveNotesIfDrawerEmpty()

    if (d.case_sos !== undefined && d.case_sos !== null) {
        merged.case_sos = d.case_sos
    } else {
        merged.case_sos = f.case_sos
    }

    if (d.case_flag !== undefined && d.case_flag !== null) {
        merged.case_flag = d.case_flag
    } else if (f.case_flag !== undefined) {
        merged.case_flag = f.case_flag
    }

    return merged
}

export function sanitizeCaseUpdatePayload(payload) {
    if (!payload || typeof payload !== 'object') {
        return {}
    }

    const cleanedPayload = { ...payload }
    delete cleanedPayload.emittedAt
    delete cleanedPayload.source
    delete cleanedPayload.updated_by_id

    return cleanedPayload
}

/**
 * @param {object|null|undefined} record
 * @returns {number}
 */
export function parseRecordUpdatedAtMs(record) {
    if (!record || typeof record !== 'object') {
        return NaN
    }
    const raw = record.updated_at
    if (raw == null || raw === '') {
        return NaN
    }
    const ms = new Date(raw).getTime()
    return Number.isFinite(ms) ? ms : NaN
}

/**
 * Skip merging when both records have comparable updated_at and incoming is older.
 * @param {object|null|undefined} existing
 * @param {object|null|undefined} incoming
 * @returns {boolean}
 */
export function shouldApplyIncomingRecord(existing, incoming) {
    const existingMs = parseRecordUpdatedAtMs(existing)
    const incomingMs = parseRecordUpdatedAtMs(incoming)
    if (!Number.isFinite(existingMs) || !Number.isFinite(incomingMs)) {
        return true
    }
    return incomingMs >= existingMs
}

function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]'
}

function cloneForSocketMerge(value) {
    if (Array.isArray(value)) {
        return value.map((item) => cloneForSocketMerge(item))
    }
    if (isPlainObject(value)) {
        const next = {}
        Object.entries(value).forEach(([key, item]) => {
            next[key] = cloneForSocketMerge(item)
        })
        return next
    }
    return value
}

export function mergeNotesArray(existing, incoming) {
    if (!Array.isArray(incoming)) {
        return Array.isArray(existing) ? existing.map((item) => ({ ...item })) : []
    }

    const allHaveId = incoming.every(
        (item) => item && typeof item === "object" && item.id != null
    )
    if (!allHaveId) {
        return incoming.map((item) => ({ ...item }))
    }

    const base = Array.isArray(existing) ? existing.map((item) => ({ ...item })) : []
    incoming.forEach((item) => {
        const index = base.findIndex((existingItem) => existingItem?.id == item.id)
        if (item?.force_remove) {
            if (index !== -1) {
                base.splice(index, 1)
            }
            return
        }
        if (index === -1) {
            base.unshift({ ...item })
            return
        }
        const alwaysApply = isDeletedRecord(item)
        if (!alwaysApply && !shouldApplyIncomingRecord(base[index], item)) {
            return
        }
        base[index] = {
            ...base[index],
            ...item,
        }
    })
    return base
}

function mergeArrayById(baseArray, incomingArray) {
    if (!Array.isArray(incomingArray)) {
        return Array.isArray(baseArray) ? cloneForSocketMerge(baseArray) : []
    }
    if (!Array.isArray(baseArray)) {
        return cloneForSocketMerge(incomingArray)
    }

    const allIncomingHaveId = incomingArray.every(
        (item) => isPlainObject(item) && item.id != null
    )
    if (!allIncomingHaveId) {
        return cloneForSocketMerge(incomingArray)
    }

    const merged = cloneForSocketMerge(baseArray)
    incomingArray.forEach((incomingItem) => {
        const existingIndex = merged.findIndex((baseItem) => {
            return isPlainObject(baseItem) && baseItem.id == incomingItem.id
        })
        if (existingIndex === -1) {
            merged.unshift(cloneForSocketMerge(incomingItem))
            return
        }
        if (!shouldApplyIncomingRecord(merged[existingIndex], incomingItem)) {
            return
        }
        merged[existingIndex] = deepMergeSocketPayload(
            merged[existingIndex],
            incomingItem
        )
    })
    return merged
}

function deepMergeSocketPayload(baseValue, incomingValue, key) {
    if (key === "notes" && Array.isArray(incomingValue)) {
        return mergeNotesArray(baseValue, incomingValue)
    }

    if (Array.isArray(incomingValue)) {
        return mergeArrayById(baseValue, incomingValue)
    }

    if (!isPlainObject(incomingValue)) {
        return incomingValue
    }

    const safeBase = isPlainObject(baseValue) ? cloneForSocketMerge(baseValue) : {}
    Object.entries(incomingValue).forEach(([childKey, value]) => {
        safeBase[childKey] = deepMergeSocketPayload(safeBase[childKey], value, childKey)
    })
    return safeBase
}

function listFlagForPractice(list, practiceId) {
    if (!list || practiceId == null || practiceId === '') {
        return 0
    }
    const value = list[practiceId] ?? list[String(practiceId)]
    return value > 0 ? 1 : 0
}

/**
 * After delete/restore, API returns case_data.case_attorney with refreshed has_hippa_list / has_lop_list.
 * Merge into attorneys[] and recompute practices.has_hippa / has_lop to match backend Practice resource.
 */
export function syncCaseAttorneyFromBatchPayload(activeCase, payload) {
    if (!activeCase || !payload?.case_attorney || typeof payload.case_attorney !== 'object') {
        return
    }

    const caseAttorney = payload.case_attorney
    if (caseAttorney.id == null) {
        return
    }

    if (!Array.isArray(activeCase.attorneys)) {
        activeCase.attorneys = []
    }

    const attorneyIndex = activeCase.attorneys.findIndex((item) => item?.id == caseAttorney.id)
    if (attorneyIndex !== -1) {
        activeCase.attorneys[attorneyIndex] = {
            ...activeCase.attorneys[attorneyIndex],
            ...caseAttorney,
        }
    } else {
        activeCase.attorneys.unshift({ ...caseAttorney })
    }

    activeCase.case_attorney = {
        ...(activeCase.case_attorney && typeof activeCase.case_attorney === 'object' ? activeCase.case_attorney : {}),
        ...caseAttorney,
    }

    if (!Array.isArray(activeCase.practices)) {
        return
    }

    activeCase.practices.forEach((practice, index) => {
        const practiceId = practice?.practice_id
        if (practiceId == null || practiceId === '') {
            return
        }
        activeCase.practices[index] = {
            ...practice,
            has_hippa: listFlagForPractice(caseAttorney.has_hippa_list, practiceId),
            has_lop: listFlagForPractice(caseAttorney.has_lop_list, practiceId),
        }
    })
}

export function recalculateCaseCounters(caseData) {
    if (!caseData || typeof caseData !== 'object') {
        return caseData
    }

    const nextData = { ...caseData }

    if (Array.isArray(nextData.mp_pip_um)) {
        nextData.mp_pip_um_count = nextData.mp_pip_um.length
    }
    if (Array.isArray(nextData.attorneys)) {
        nextData.attorneys_count = nextData.attorneys.length
    }
    if (Array.isArray(nextData.liabilities)) {
        nextData.liabilities_count = nextData.liabilities.length
    }
    if (Array.isArray(nextData.major_meds)) {
        nextData.major_meds_count = nextData.major_meds.length
    }
    if (Array.isArray(nextData.appointments)) {
        nextData.appointments_count = nextData.appointments.length
    }
    if (Array.isArray(nextData.case_sos)) {
        nextData.case_sos_count = nextData.case_sos.length
    }
    if (Array.isArray(nextData.practices)) {
        nextData.practices_count = nextData.practices.length
    }

    return nextData
}

export function mergeCaseUpdateData(baseCaseData, incomingPayload) {
    const base = baseCaseData && typeof baseCaseData === 'object' ? baseCaseData : {}
    const payload = sanitizeCaseUpdatePayload(incomingPayload)

    return recalculateCaseCounters(deepMergeSocketPayload(base, payload))
}
